import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { logger } from "@games/logger-client";

interface DirectoryInfo {
  name: string;
  type: string;
  mtime: string;
}

const Builds: React.FC = () => {
  const [directories, setDirectories] = useState<DirectoryInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    logger.componentMount("Builds");

    const fetchDirectories = async () => {
      setLoading(true);
      try {
        // Пытаемся получить список директорий через nginx автоиндекс
        logger.debug("📂 Fetching Unity build directories");
        const response = await fetch("/UnityBuild/");

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        // Фильтруем только директории и сортируем по дате (более новые вверху)
        const dirs = data
          .filter((item: DirectoryInfo) => item.type === "directory")
          .sort((a: DirectoryInfo, b: DirectoryInfo) => {
            // Сортировка по убыванию даты (новые сверху)
            return new Date(b.mtime).getTime() - new Date(a.mtime).getTime();
          });

        logger.debug(`📦 Found ${dirs.length} build directories`);
        setDirectories(dirs);
        setError(null);
      } catch (err) {
        logger.warn("🚫 Error fetching build directories via nginx:", err);
        setError("Не удалось получить список сборок через стандартный метод");

        // Альтернативный метод для локальной разработки: используем захардкоженный список
        const now = new Date();
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const lastWeek = new Date(now);
        lastWeek.setDate(lastWeek.getDate() - 7);

        const localBuilds: DirectoryInfo[] = [
          { name: "Fake", type: "directory", mtime: now.toUTCString() },
          { name: "dev-build-1", type: "directory", mtime: now.toUTCString() },
          { name: "20.03.25", type: "directory", mtime: yesterday.toUTCString() },
          { name: "local-test", type: "directory", mtime: lastWeek.toUTCString() },
        ];

        // Сортируем локальный список тоже
        localBuilds.sort((a, b) => {
          return new Date(b.mtime).getTime() - new Date(a.mtime).getTime();
        });

        logger.debug("📜 Using fallback local build list");
        setDirectories(localBuilds);
      } finally {
        setLoading(false);
      }
    };

    fetchDirectories();

    return () => {
      logger.componentUnmount("Builds");
    };
  }, []);

  const handleBuildSelect = (buildName: string) => {
    logger.navigation(`📌 Build selected: ${buildName}`);
    navigate(`/tokens?build=${encodeURIComponent(buildName)}`);
  };

  const buttonStyle = {
    display: "block",
    margin: "10px 0",
    padding: "10px 15px",
    backgroundColor: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    width: "300px",
    fontSize: "16px",
  };

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <h1>Доступные сборки:</h1>

      {loading && <p>Загрузка списка сборок...</p>}

      {error && <p style={{ color: "red" }}>{error}</p>}

      {!loading && directories.length === 0 && !error && (
        <p>Сборки не найдены</p>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {directories.map((dir) => (
          <button
            key={dir.name}
            style={buttonStyle}
            onClick={() => handleBuildSelect(dir.name)}
          >
            {dir.name} ({new Date(dir.mtime).toLocaleDateString()})
          </button>
        ))}
      </div>
    </div>
  );
};

export default Builds;
